/**
 * usePaymentSubmit — orchestrates the four-step payment flow.
 *
 * Steps (per data-layer handoff §4):
 *   1. `elements.submit()` — validate the form client-side.
 *   2. `stripe.createPaymentMethod({ elements })` — get a payment method id.
 *   3. POST /payment/charge or /checkin/payment — backend creates the intent.
 *   4. `stripe.confirmPayment({ clientSecret, ... })` — confirm and handle 3DS.
 *
 * The hook returns a single `submit()` function plus rich state for the UI.
 * Errors at any step bubble through `error` with a categorized `kind` so
 * the form can show appropriate copy (form-validation vs card-declined vs
 * server-rejected vs network).
 *
 * The hook does NOT navigate on success — the caller decides where to go,
 * because balance-pay and precollect routes have different success URLs.
 * The caller receives `{ paymentSessionId, amountCents }` to forward via
 * navigation state to the success page.
 */
import { useCallback, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import {
  useCreateBalanceIntent,
  useCreatePrecollectIntent,
} from '../api/queries';
import { ApiError } from '../api/client';
import { withBasePath } from '../lib/basePath';
import type {
  CreateBalanceIntentRequest,
  CreatePrecollectIntentRequest,
} from '../api/types';

export type PaymentRequest =
  | { kind: 'balance'; request: Omit<CreateBalanceIntentRequest, 'stripe_payment_method_id'> }
  | { kind: 'precollect'; request: Omit<CreatePrecollectIntentRequest, 'stripe_payment_method_id'> };

export type PaymentErrorKind =
  | 'stripe_not_ready'
  | 'form_validation'
  | 'create_payment_method'
  | 'backend_rejected'
  | 'card_declined'
  | 'amount_mismatch'
  // As of backend v0.7.1, these two are discriminated by first-class
  // error codes: PATIENT_PORTAL_PARTIAL_ESTIMATE_REJECTED and
  // PATIENT_PORTAL_SUB_STRIPE_MINIMUM. A defensive substring-match on
  // field_errors[*].message remains in mapBackendError to bridge any
  // pre-v0.7.1 deploys still in the loop; once every environment is on
  // v0.7.1+, that fallback can be deleted.
  | 'partial_estimate_rejected'
  | 'sub_stripe_minimum'
  | 'confirm'
  | 'unknown';

export interface PaymentError {
  kind: PaymentErrorKind;
  message: string;
  /** Server-side error_code if the backend rejected. */
  errorCode?: string;
}

export interface PaymentResult {
  ok: true;
  paymentSessionId: string;
  paymentIntentId: string;
  amountCents: number;
}

export interface PaymentSubmitState {
  isSubmitting: boolean;
  error: PaymentError | null;
}

export function usePaymentSubmit() {
  const stripe = useStripe();
  const elements = useElements();
  const createBalance = useCreateBalanceIntent();
  const createPrecollect = useCreatePrecollectIntent();

  const [state, setState] = useState<PaymentSubmitState>({
    isSubmitting: false,
    error: null,
  });

  const submit = useCallback(
    async (
      req: PaymentRequest,
      options?: { paymentMethodId?: string }
    ): Promise<PaymentResult | null> => {
      if (!stripe || !elements) {
        const err: PaymentError = {
          kind: 'stripe_not_ready',
          message: "Payment form isn't ready yet. Please wait a moment.",
        };
        setState({ isSubmitting: false, error: err });
        return null;
      }

      setState({ isSubmitting: true, error: null });

      let paymentMethodId = options?.paymentMethodId;

      if (!paymentMethodId) {
        // Step 1 — client-side validation of the Payment Element fields.
        const submitResult = await elements.submit();
        if (submitResult.error) {
          const err: PaymentError = {
            kind: 'form_validation',
            message: submitResult.error.message ?? 'Please check the card details.',
          };
          setState({ isSubmitting: false, error: err });
          return null;
        }

        // Step 2 — create payment method.
        const pmResult = await stripe.createPaymentMethod({ elements });
        if (pmResult.error) {
          const err: PaymentError = {
            kind: 'create_payment_method',
            message: pmResult.error.message ?? 'Could not process the card.',
          };
          setState({ isSubmitting: false, error: err });
          return null;
        }
        paymentMethodId = pmResult.paymentMethod.id;
      }

      // Step 3 — backend creates the PaymentIntent.
      let intent;
      try {
        if (req.kind === 'balance') {
          intent = await createBalance.mutateAsync({
            ...req.request,
            stripe_payment_method_id: paymentMethodId,
          });
        } else {
          intent = await createPrecollect.mutateAsync({
            ...req.request,
            stripe_payment_method_id: paymentMethodId,
          });
        }
      } catch (e) {
        const err = mapBackendError(e);
        setState({ isSubmitting: false, error: err });
        return null;
      }

      // Step 4 — confirm with Stripe. `redirect: 'if_required'` keeps the
      // patient on-page when no 3DS challenge is needed; if a redirect is
      // required Stripe does the dance and brings them back.
      const confirmResult = await stripe.confirmPayment({
        clientSecret: intent.client_secret,
        confirmParams: {
          // Must include the base path: Stripe navigates the browser
          // here after a 3DS challenge, and `origin + '/success'` would
          // land on the host root rather than back inside the portal.
          return_url: window.location.origin + withBasePath('/success'),
        },
        redirect: 'if_required',
      });

      if (confirmResult.error) {
        // Card declines and 3DS failures land here.
        const declineLikeCodes = new Set([
          'card_declined',
          'expired_card',
          'incorrect_cvc',
          'insufficient_funds',
        ]);
        const code = confirmResult.error.code;
        const kind: PaymentErrorKind = code && declineLikeCodes.has(code)
          ? 'card_declined'
          : 'confirm';
        const err: PaymentError = {
          kind,
          message:
            confirmResult.error.message ??
            'We could not confirm the payment. Please try again.',
        };
        setState({ isSubmitting: false, error: err });
        return null;
      }

      // Success path — Stripe says the charge succeeded. The webhook will
      // post to the ledger asynchronously; we show optimistic success.
      const paymentIntent = confirmResult.paymentIntent;
      const amountCents = paymentIntent?.amount ?? 0;

      setState({ isSubmitting: false, error: null });

      return {
        ok: true,
        paymentSessionId: intent.payment_session_id,
        paymentIntentId: intent.payment_intent_id,
        amountCents,
      };
    },
    [stripe, elements, createBalance, createPrecollect],
  );

  return {
    submit,
    isSubmitting: state.isSubmitting,
    error: state.error,
    /** True when Stripe is ready to be used. */
    ready: stripe !== null && elements !== null,
  };
}

function mapBackendError(e: unknown): PaymentError {
  if (e instanceof ApiError) {
    const code = e.problem.error_code;
    // Primary: error_code switch (backend v0.7.1+). Backend confirmed in
    // the v0.7.1 reply that PARTIAL_ESTIMATE_REJECTED and SUB_STRIPE_MINIMUM
    // are now first-class codes. AMOUNT_MISMATCH is no longer a generic
    // 422 bucket — it specifically means "submitted total doesn't match
    // server's computed total" (the original semantic).
    if (code === 'PATIENT_PORTAL_PARTIAL_ESTIMATE_REJECTED') {
      return {
        kind: 'partial_estimate_rejected',
        message:
          'Please pay the full estimated amount, or skip the estimate to pay only your past balance.',
        errorCode: code,
      };
    }
    if (code === 'PATIENT_PORTAL_SUB_STRIPE_MINIMUM') {
      return {
        kind: 'sub_stripe_minimum',
        message:
          "This amount is too small to charge online. Please pay at the clinic.",
        errorCode: code,
      };
    }
    if (code === 'PATIENT_PORTAL_AMOUNT_MISMATCH') {
      // Defensive: if a pre-v0.7.1 backend is still in the loop, fall
      // back to substring-matching the field_errors[*].message for the
      // discriminator words ("partial", "minimum"). Production v0.7.1+
      // will short-circuit this branch via the codes above. Safe to
      // delete this fallback once every environment has rolled to v0.7.1.
      const messages = (e.problem.field_errors ?? [])
        .map((f) => (f.message ?? '').toLowerCase())
        .join(' ');
      if (messages.includes('partial')) {
        return {
          kind: 'partial_estimate_rejected',
          message:
            'Please pay the full estimated amount, or skip the estimate to pay only your past balance.',
          errorCode: code,
        };
      }
      if (messages.includes('minimum')) {
        return {
          kind: 'sub_stripe_minimum',
          message:
            "This amount is too small to charge online. Please pay at the clinic.",
          errorCode: code,
        };
      }
      // Genuine AMOUNT_MISMATCH (server-recomputed total disagrees with
      // submitted) — staff updated the estimate while patient was paying.
      return {
        kind: 'amount_mismatch',
        message:
          'The balance changed while you were paying. Please refresh and try again.',
        errorCode: code,
      };
    }
    if (code === 'PATIENT_PORTAL_CARD_DECLINED') {
      return {
        kind: 'card_declined',
        message: 'Your card was declined. Please try another.',
        errorCode: code,
      };
    }
    return {
      kind: 'backend_rejected',
      message: e.problem.detail || 'Payment could not be created.',
      errorCode: code,
    };
  }
  return {
    kind: 'unknown',
    message: 'Something went wrong. Please try again.',
  };
}
